import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { useDeleteEntityDocumentMutation } from '@/modules/entity/queries/entity.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { ChangeShareholderComponentProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Dropzone } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { UpdateEntityRequest } from 'api';

interface Document {
  data: string;
  type: string;
  index?: string;
}

interface IdentificationDocumentsFormik {
  documents: Document[];
}

const validationSchema = yup.object({
  documents: yup.array().of(
    yup
      .object({
        data: yup.string().trim().required(),
        type: yup.string().trim().required(),
        index: yup.string().trim(),
      })
      .required('Please upload document'),
  ),
});
const validationSchemaIfOptional = yup.object({
  documents: yup.array().of(
    yup
      .object({
        data: yup.string().trim().required(),
        type: yup.string().trim().required(),
        index: yup.string().trim(),
      })
      .optional(),
  ),
});

export const IdentificationDocumentsHolder = ({
  moveForward,
  entity_type_prefix,
  label,
  conditionCallback,
  person_details_key,
  uuidIndex,
  item_entity_type,
  title,
  allowSkip,
  titleHint,
}: ChangeShareholderComponentProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const [isFileLoading, setIsFileLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const docsNeeded = useMemo(() => {
    return (
      (conditionCallback &&
        dataHolder?.data?.new_data?.entity_data &&
        conditionCallback(dataHolder?.data?.new_data?.entity_data)) ??
      true
    );
  }, [dataHolder?.data?.new_data?.entity_data, conditionCallback]);

  const documentType = useMemo(
    () =>
      person_details_key
        ? `${item_entity_type}_entity_details_identification_document`
        : `${entity_type_prefix}_identification_document`,
    [entity_type_prefix, item_entity_type, person_details_key],
  );

  const getInitialValues = (): IdentificationDocumentsFormik => {
    return {
      documents: (dataHolder?.data?.new_data?.entity_data?.document_files || [])
        ?.filter(
          item =>
            (item.type === documentType && !person_details_key) || //it's correct for multi company
            (item.index === `${person_details_key}_${uuidIndex}` &&
              !!person_details_key &&
              !!uuidIndex),
        )
        ?.map(item => ({
          data: item.url ?? '',
          type: item.type ?? '',
          index: item.index ?? '',
        })),
    };
  };

  const formik = useFormik<IdentificationDocumentsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: docsNeeded ? validationSchema : validationSchemaIfOptional,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      setDirty(false);
      if (docsNeeded) {
        if (values.documents.length < 1) {
          formik.setFieldError('documents', 'Please upload document');
          return;
        }
      }

      const documents =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];
      if (documents?.length > 0) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            documents,
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation();

  const onFileAccepted = useCallback(
    async (file: File) => {
      setIsFileLoading(true);

      const base64 = await fileToBase64(file, true);

      await formik.setFieldTouched('documents', true);
      await formik.setFieldValue('documents', [
        {
          type: documentType,
          data: base64,
        },
      ]);
      formik.setFieldError('documents', '');

      setIsFileLoading(false);
    },
    [documentType, formik],
  );

  const onFileAcceptedForSubRoads = useCallback(
    async (file: File) => {
      setIsFileLoading(true);

      const base64 = await fileToBase64(file, true);

      await formik.setFieldValue('documents', [
        {
          type: documentType,
          index: `${person_details_key}_${uuidIndex}`,
          data: base64,
        },
      ]);
      await formik.setFieldTouched('documents', true);
      formik.setFieldError('documents', '');

      setIsFileLoading(false);
    },
    [documentType, formik, person_details_key, uuidIndex],
  );

  const onFileClear = useCallback(async () => {
    // const current = formik.values.documents.find(doc => doc.type === documentType);
    // if (current?.data?.includes('http')) {
    //   const index =
    //     person_details_key && uuidIndex ? `${person_details_key}_${uuidIndex}` : undefined;
    //   await deleteDocument({ entity_id: +entity_id || 0, type: documentType, index });
    // }

    await formik.setFieldTouched('documents', true);
    await formik.setFieldValue('documents', []);
    formik.setFieldError('documents', '');
    setDocumentUrl('');
  }, [entity_id, deleteDocument, documentType, formik, person_details_key, uuidIndex]);

  useEffect(() => {
    if (
      dataHolder?.data?.new_data?.entity_data &&
      dataHolder?.data?.new_data?.entity_data?.document_files &&
      !person_details_key
    ) {
      setDocumentUrl(
        dataHolder?.data?.new_data?.entity_data?.document_files.find(
          ({ type }) => type === documentType,
        )?.url || '',
      );
    } else if (
      dataHolder?.data?.new_data?.entity_data &&
      dataHolder?.data?.new_data?.entity_data?.document_files &&
      person_details_key &&
      uuidIndex
    ) {
      setDocumentUrl(
        dataHolder?.data?.new_data?.entity_data?.document_files.find(
          ({ index }) => index === `${person_details_key}_${uuidIndex}`,
        )?.url || '',
      );
    }
  }, [
    dataHolder?.data?.new_data?.entity_data,
    uuidIndex,
    person_details_key,
    entity_type_prefix,
    documentType,
  ]);

  return (
    <OnboardingHolderWrapper
      title={title || 'Identification documents'}
      titleHint={titleHint}
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading || isFileLoading}
    >
      <div className="w-full flex flex-col space-y-4">
        <div className="w-full flex flex-col space-y-1">
          <p className="text-xl">
            {label} {!docsNeeded && '(Optional)'}
          </p>
          <p className="text-sm text-gray-600">JPG, PNG or PDF. Up to 10 mb</p>
        </div>
        <Dropzone
          onFileAccepted={person_details_key ? onFileAcceptedForSubRoads : onFileAccepted}
          onFileClear={onFileClear}
          value={documentUrl || formik.values.documents[0]?.data}
          error={formik.errors.documents as string}
          loading={isFileLoading || isDocumentDeletion}
        />
      </div>
    </OnboardingHolderWrapper>
  );
};
